import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, LogOut, FileText, UserPlus, Edit, Trash2, Clock, Filter } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function HistorialCambios({ user, onLogout }) {
  const navigate = useNavigate();
  const [logs, setLogs] = useState([]);
  const [filteredLogs, setFilteredLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [moduloFilter, setModuloFilter] = useState('todos');

  useEffect(() => {
    loadLogs();
  }, []);

  useEffect(() => {
    filterLogs();
  }, [moduloFilter, logs]);

  const loadLogs = async () => {
    try {
      const response = await axios.get(`${API}/changelog?limit=200`);
      setLogs(response.data);
    } catch (error) {
      console.error('Error al cargar historial:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterLogs = () => {
    if (moduloFilter === 'todos') {
      setFilteredLogs(logs);
    } else {
      setFilteredLogs(logs.filter(log => log.modulo === moduloFilter));
    }
  };

  const getTipoCambioIcon = (tipo) => {
    switch (tipo) {
      case 'crear':
        return <UserPlus className="w-4 h-4 text-green-600" />;
      case 'editar':
        return <Edit className="w-4 h-4 text-blue-600" />;
      case 'eliminar':
        return <Trash2 className="w-4 h-4 text-red-600" />;
      default:
        return <FileText className="w-4 h-4 text-gray-600" />;
    }
  };

  const getTipoCambioBadge = (tipo) => {
    const badges = {
      'crear': 'bg-green-100 text-green-800 border-green-200',
      'editar': 'bg-blue-100 text-blue-800 border-blue-200',
      'eliminar': 'bg-red-100 text-red-800 border-red-200',
    };
    
    return (
      <Badge className={`${badges[tipo] || 'bg-gray-100 text-gray-800 border-gray-200'} border flex items-center gap-1`}>
        {getTipoCambioIcon(tipo)}
        {tipo.charAt(0).toUpperCase() + tipo.slice(1)}
      </Badge>
    );
  };

  const getModuloBadge = (modulo) => {
    const badges = {
      'trabajador': 'bg-emerald-100 text-emerald-800',
      'usuario': 'bg-purple-100 text-purple-800',
      'documento': 'bg-orange-100 text-orange-800',
      'notificacion': 'bg-yellow-100 text-yellow-800',
    };
    
    return (
      <Badge className={badges[modulo] || 'bg-gray-100 text-gray-800'}>
        {modulo.charAt(0).toUpperCase() + modulo.slice(1)}
      </Badge>
    );
  };

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Card */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card className="p-4 border-0 shadow-md">
            <div className="text-center">
              <Clock className="w-8 h-8 mx-auto text-emerald-600 mb-2" />
              <p className="text-2xl font-bold text-emerald-900">{logs.length}</p>
              <p className="text-sm text-gray-600">Total de Cambios</p>
            </div>
          </Card>
          <Card className="p-4 border-0 shadow-md">
            <div className="text-center">
              <UserPlus className="w-8 h-8 mx-auto text-green-600 mb-2" />
              <p className="text-2xl font-bold text-green-900">
                {logs.filter(l => l.tipo_cambio === 'crear').length}
              </p>
              <p className="text-sm text-gray-600">Creaciones</p>
            </div>
          </Card>
          <Card className="p-4 border-0 shadow-md">
            <div className="text-center">
              <Edit className="w-8 h-8 mx-auto text-blue-600 mb-2" />
              <p className="text-2xl font-bold text-blue-900">
                {logs.filter(l => l.tipo_cambio === 'editar').length}
              </p>
              <p className="text-sm text-gray-600">Ediciones</p>
            </div>
          </Card>
          <Card className="p-4 border-0 shadow-md">
            <div className="text-center">
              <Trash2 className="w-8 h-8 mx-auto text-red-600 mb-2" />
              <p className="text-2xl font-bold text-red-900">
                {logs.filter(l => l.tipo_cambio === 'eliminar').length}
              </p>
              <p className="text-sm text-gray-600">Eliminaciones</p>
            </div>
          </Card>
        </div>

        {/* Filters */}
        <Card className="p-4 mb-6 border-0 shadow-md">
          <div className="flex items-center gap-4">
            <Filter className="w-5 h-5 text-gray-500" />
            <Select value={moduloFilter} onValueChange={setModuloFilter}>
              <SelectTrigger className="w-64">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todos los Módulos</SelectItem>
                <SelectItem value="trabajador">Trabajadores</SelectItem>
                <SelectItem value="usuario">Usuarios</SelectItem>
                <SelectItem value="documento">Documentos</SelectItem>
                <SelectItem value="notificacion">Notificaciones</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-sm text-gray-600">
              Mostrando {filteredLogs.length} de {logs.length} cambios
            </p>
          </div>
        </Card>

        {/* Changelog Table */}
        <Card className="border-0 shadow-md">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Fecha y Hora</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Módulo</TableHead>
                  <TableHead>Descripción</TableHead>
                  <TableHead>Usuario</TableHead>
                  <TableHead>Detalles</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-gray-500">
                      Cargando historial...
                    </TableCell>
                  </TableRow>
                ) : filteredLogs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-gray-500">
                      No hay cambios registrados
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredLogs.map((log) => (
                    <TableRow key={log.id}>
                      <TableCell className="font-mono text-sm">
                        {formatDateTime(log.created_at)}
                      </TableCell>
                      <TableCell>{getTipoCambioBadge(log.tipo_cambio)}</TableCell>
                      <TableCell>{getModuloBadge(log.modulo)}</TableCell>
                      <TableCell className="font-medium">{log.descripcion}</TableCell>
                      <TableCell>{log.usuario_nombre}</TableCell>
                      <TableCell className="text-sm text-gray-600">
                        {log.detalles || '-'}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </Card>
      </main>
    </div>
  );
}
